import { parseUnits } from 'viem'

import { SwapDirection, SwapRouter } from '~/components/forms/swap/utils'
import { BRIDGE_FEE_PERCENT } from '~/constants/addresses'
import {
  BackendTransaction,
  BackendTransactionCall,
  BasicTransactionType,
  BatchOperationInput,
  EarnAction,
  MetadataPayloads,
  MetadataType,
  TransactionRecipient,
  TransactionStatus,
} from '~/types'
import { Address } from '~/utils/address'
import { getBridgeProviderType } from '~/utils/bridgeProvider'

export const isBatchTransaction = (
  transaction: Pick<BackendTransaction, 'isSplitsTransaction'> & {
    types?: BasicTransactionType[]
  },
) =>
  transaction.isSplitsTransaction &&
  !!transaction.types?.includes(BasicTransactionType.Batch)

export const isDraftBatchTransaction = (
  transaction: Pick<BackendTransaction, 'isSplitsTransaction' | 'status'> & {
    types?: BasicTransactionType[]
  },
) =>
  isBatchTransaction(transaction) &&
  transaction.status === TransactionStatus.Drafted

// Backend decoder populates titleChunks/functionSignature/contractName at load time,
// so the client only sends {to, value, calldata}.
export const buildBatchOperation = ({
  name,
  types,
  calls,
  walletConnectAppName,
  walletConnectAppDomain,
}: {
  name: string
  types: BasicTransactionType[]
  calls: BackendTransactionCall[]
  walletConnectAppName?: string
  walletConnectAppDomain?: string
}): BatchOperationInput => ({
  name,
  types,
  calls: calls.map((call) => ({
    to: call.to,
    calldata: call.data,
    value: call.value.toString(),
  })),
  walletConnectAppName,
  walletConnectAppDomain,
})

type TokenSide = { token: Address.Type; amount: string; chainId: number }

// Shared swap metadata section: buy = destination/transfer, sell = source
const buildSwapMetadataSection = ({
  buy,
  buyAmount,
  sell,
  sellAmount,
  provider,
  direction,
  slippage,
  isPrivate,
}: {
  buy: Pick<TokenSide, 'token'>
  buyAmount: string
  sell: Pick<TokenSide, 'token'>
  sellAmount: string
  provider: SwapRouter
  direction: SwapDirection
  slippage?: string
  isPrivate?: boolean
}): MetadataPayloads => ({
  type: MetadataType.Swap,
  buyTokenAddress: buy.token,
  buyAmount,
  sellAmount,
  sellTokenAddress: sell.token,
  direction,
  provider,
  slippage,
  private: isPrivate,
})

// Shared bridge metadata section: buy side carries destination chain
const buildBridgeMetadataSection = ({
  buy,
  buyAmount,
  sell,
  sellAmount,
  providerTransactionId,
  provider,
  settlementRouter,
  isPrivate,
}: {
  buy: TokenSide
  buyAmount: string
  sell: Pick<TokenSide, 'token'>
  sellAmount: string
  providerTransactionId?: string
  provider?: SwapRouter
  settlementRouter?: SwapRouter
  isPrivate?: boolean
}): MetadataPayloads => ({
  type: MetadataType.Bridge,
  destinationChainId: buy.chainId,
  sellAmount,
  sellTokenAddress: sell.token,
  providerTransactionId,
  providerType: getBridgeProviderType(settlementRouter ?? provider),
  private: isPrivate,
  buyTokenAddress: buy.token,
  buyAmount,
})

const buildTransferMetadataSection = ({
  recipients,
  tokenDecimals,
  tokenAddress,
}: {
  recipients: TransactionRecipient[]
  tokenDecimals: number
  tokenAddress: Address.Type
}): MetadataPayloads => ({
  type: MetadataType.Transfer,
  recipients: recipients.map((recipient) => ({
    address: recipient.address,
    amount: parseUnits(recipient.amount, tokenDecimals).toString(),
    compliantPaymentRelationId: recipient.compliantPaymentRelationId,
  })),
  tokenAddress,
})

const bankTransferMetadata = (isOfframp: boolean): MetadataPayloads[] =>
  isOfframp
    ? [{ type: MetadataType.BankTransfer, feePercent: BRIDGE_FEE_PERCENT }]
    : []

export const buildSwapTransferBatchMetadata = ({
  recipients,
  transferToken,
  transferTokenDecimals,
  sellToken,
  sellTokenDecimals,
  transactionChainId,
  transactionTypes,
  providerTransactionId,
  swapRouter,
  settlementRouter,
  swapSlippage,
  earnToken,
  isOfframp,
  isPrivate,
}: {
  recipients: TransactionRecipient[]
  transferToken: TokenSide
  transferTokenDecimals: number
  sellToken?: TokenSide
  sellTokenDecimals?: number
  transactionChainId: number
  transactionTypes: BasicTransactionType[]
  providerTransactionId?: string
  swapRouter?: SwapRouter
  settlementRouter?: SwapRouter
  swapSlippage?: string
  earnToken?: {
    token: Address.Type
    amount?: string
    action: EarnAction
    chainId: number
  }
  isOfframp: boolean
  isPrivate?: boolean
}): MetadataPayloads[] => {
  const buyAmount = parseUnits(
    transferToken.amount,
    transferTokenDecimals,
  ).toString()
  const sellAmount =
    sellToken && sellTokenDecimals !== undefined
      ? parseUnits(sellToken.amount, sellTokenDecimals).toString()
      : undefined

  return [
    buildTransferMetadataSection({
      recipients,
      tokenDecimals: transferTokenDecimals,
      tokenAddress: transferToken.token,
    }),
    ...(transactionTypes.includes(BasicTransactionType.Swap) &&
    sellToken &&
    sellAmount &&
    swapRouter
      ? [
          buildSwapMetadataSection({
            buy: transferToken,
            buyAmount,
            sell: sellToken,
            sellAmount,
            provider: swapRouter,
            direction: SwapDirection.BUY,
            slippage: swapSlippage,
            isPrivate,
          }),
        ]
      : []),
    // A provider transaction id exists exactly when a provider settles the
    // swap in a separate leg (cross-chain bridges, NearIntents even
    // same-chain) — the backend verifies that settlement via this section,
    // so it's keyed on the id rather than the Bridge type tag.
    ...(providerTransactionId && sellToken && sellAmount
      ? [
          buildBridgeMetadataSection({
            buy: transferToken,
            buyAmount,
            sell: sellToken,
            sellAmount,
            providerTransactionId,
            provider: swapRouter,
            settlementRouter,
            isPrivate,
          }),
        ]
      : []),
    ...(transactionTypes.includes(BasicTransactionType.Earn) &&
    earnToken &&
    sellToken &&
    sellTokenDecimals !== undefined &&
    sellAmount
      ? [
          {
            type: MetadataType.Earn,
            action: earnToken.action,
            // assets are denominated in whichever token leaves the user: buy on DEPOSIT, sell on WITHDRAW
            assets: parseUnits(
              earnToken.amount ?? '0',
              earnToken.action === EarnAction.DEPOSIT
                ? transferTokenDecimals
                : sellTokenDecimals,
            ).toString(),
            asset: earnToken.token,
            vault:
              earnToken.action === EarnAction.DEPOSIT
                ? transferToken.token
                : sellToken.token,
            shares:
              earnToken.action === EarnAction.DEPOSIT ? buyAmount : sellAmount,
            chainId: transactionChainId,
          } satisfies MetadataPayloads,
        ]
      : []),
    ...bankTransferMetadata(isOfframp),
  ]
}

export const buildSwapBatchMetadata = ({
  sourceToken,
  sourceTokenDecimals,
  destinationToken,
  destinationTokenDecimals,
  transactionTypes,
  providerTransactionId,
  routerType,
  settlementRouter,
  direction,
  slippage,
  earnData,
}: {
  sourceToken: TokenSide
  sourceTokenDecimals: number
  destinationToken: TokenSide
  destinationTokenDecimals: number
  transactionTypes: BasicTransactionType[]
  providerTransactionId?: string
  routerType: SwapRouter
  settlementRouter?: SwapRouter
  direction: SwapDirection
  slippage?: string
  earnData?: {
    token: Address.Type
    amount: string
    action: EarnAction
    chainId: number
  }
}): MetadataPayloads[] => {
  const buyAmount = parseUnits(
    destinationToken.amount,
    destinationTokenDecimals,
  ).toString()
  const sellAmount = parseUnits(
    sourceToken.amount,
    sourceTokenDecimals,
  ).toString()

  return [
    ...(transactionTypes.includes(BasicTransactionType.Swap)
      ? [
          buildSwapMetadataSection({
            buy: destinationToken,
            buyAmount,
            sell: sourceToken,
            sellAmount,
            provider: routerType,
            direction,
            slippage,
          }),
        ]
      : []),
    // Keyed on the provider transaction id, not the Bridge type tag — see
    // buildSwapTransferBatchMetadata.
    ...(providerTransactionId
      ? [
          buildBridgeMetadataSection({
            buy: destinationToken,
            buyAmount,
            sell: sourceToken,
            sellAmount,
            providerTransactionId,
            provider: routerType,
            settlementRouter,
          }),
        ]
      : []),
    ...(transactionTypes.includes(BasicTransactionType.Earn) && earnData
      ? [
          {
            type: MetadataType.Earn,
            action: earnData.action,
            assets: parseUnits(earnData.amount, sourceTokenDecimals).toString(),
            asset: earnData.token,
            vault:
              earnData.action === EarnAction.DEPOSIT
                ? destinationToken.token
                : sourceToken.token,
            shares:
              earnData.action === EarnAction.DEPOSIT ? buyAmount : sellAmount,
            chainId: sourceToken.chainId,
          } satisfies MetadataPayloads,
        ]
      : []),
  ]
}

export const buildEarnBatchMetadata = ({
  action,
  chainId,
  assetToken,
  assetAmount,
  assetTokenDecimals,
  vaultToken,
  vaultAmount,
  vaultTokenDecimals,
  sellToken,
  sellAmount,
  sellTokenDecimals,
  swapRouter,
  swapSlippage,
}: {
  action: EarnAction
  chainId: number
  assetToken: Address.Type
  assetAmount: string
  assetTokenDecimals: number
  vaultToken: Address.Type
  vaultAmount: string
  vaultTokenDecimals: number
  sellToken?: Address.Type
  sellAmount?: string
  sellTokenDecimals?: number
  swapRouter?: SwapRouter
  swapSlippage?: string
}): MetadataPayloads[] => {
  const normalizedAssets = parseUnits(
    assetAmount,
    assetTokenDecimals,
  ).toString()
  const normalizedShares = parseUnits(
    vaultAmount,
    vaultTokenDecimals,
  ).toString()

  return [
    {
      type: MetadataType.Earn,
      action,
      assets: normalizedAssets,
      asset: assetToken,
      vault: vaultToken,
      shares: normalizedShares,
      chainId,
    },
    ...(action === EarnAction.DEPOSIT &&
    sellToken &&
    !Address.equal(sellToken, assetToken) &&
    sellAmount &&
    sellTokenDecimals !== undefined &&
    swapRouter
      ? [
          buildSwapMetadataSection({
            buy: { token: assetToken },
            buyAmount: normalizedAssets,
            sell: { token: sellToken },
            sellAmount: parseUnits(sellAmount, sellTokenDecimals).toString(),
            provider: swapRouter,
            direction: SwapDirection.SELL,
            slippage: swapSlippage,
          }),
        ]
      : []),
  ]
}
